import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { twoFactorApi, authApi, settingsApi, statementsApi } from '../services/api';
import { toast } from 'sonner';
import { Shield, KeyRound, Lock, Bell, FileText, Loader2, CheckCircle, XCircle, Download, UserCircle } from 'lucide-react';

const ProfileSection = () => {
  const { admin, refreshAdmin } = useAuth();
  const [name, setName] = useState(admin?.name || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.updateProfile({ name });
      toast.success('Profil mis a jour');
      await refreshAdmin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCircle className="w-5 h-5 text-[#0066FF]" />
          Mon profil
        </CardTitle>
        <CardDescription>Modifiez vos informations personnelles</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={admin?.email || ''} disabled className="bg-slate-50" />
          </div>
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name" />
          </div>
          <Button type="submit" disabled={loading} data-testid="profile-save-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const TwoFactorSection = () => {
  const { admin, refreshAdmin } = useAuth();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupMode, setSetupMode] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await twoFactorApi.setup();
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setSetupMode(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la configuration 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await twoFactorApi.verify({ totp_code: verifyCode });
      toast.success('2FA active avec succes');
      setSetupMode(false);
      setQrCode('');
      setSecret('');
      setVerifyCode('');
      await refreshAdmin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await twoFactorApi.disable({ totp_code: disableCode });
      toast.success('2FA desactive');
      setDisableCode('');
      await refreshAdmin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="w-5 h-5 text-[#0066FF]" />
              Authentification a deux facteurs (2FA)
            </CardTitle>
            <CardDescription className="mt-1">Securisez votre compte avec un code TOTP</CardDescription>
          </div>
          <Badge className={admin?.totp_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
            {admin?.totp_enabled ? 'Active' : 'Desactive'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {admin?.totp_enabled ? (
          <form onSubmit={handleDisable} className="space-y-4">
            <p className="text-sm text-slate-600">Le 2FA est actif. Pour le desactiver, entrez votre code actuel.</p>
            <div className="flex gap-3">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Code 2FA"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="max-w-[200px]"
                data-testid="2fa-disable-code"
              />
              <Button type="submit" variant="destructive" disabled={loading || disableCode.length !== 6} data-testid="2fa-disable-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desactiver'}
              </Button>
            </div>
          </form>
        ) : setupMode ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Scannez le QR code avec votre application (Google Authenticator, Authy, etc.)</p>
            <div className="flex flex-col items-center gap-4 p-4 bg-slate-50 rounded-lg">
              {qrCode && <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" data-testid="2fa-qr-code" />}
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Cle secrete (saisie manuelle) :</p>
                <code className="text-xs bg-white px-3 py-1 rounded border border-slate-200 font-mono select-all">{secret}</code>
              </div>
            </div>
            <form onSubmit={handleVerify} className="flex gap-3">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Code de verification"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="max-w-[200px]"
                data-testid="2fa-verify-code"
              />
              <Button type="submit" disabled={loading || verifyCode.length !== 6} data-testid="2fa-verify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setSetupMode(false)}>Annuler</Button>
            </form>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-600 mb-4">Activez le 2FA pour renforcer la securite de votre compte.</p>
            <Button onClick={handleSetup} disabled={loading} data-testid="2fa-setup-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Configurer le 2FA
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PasswordSection = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Mot de passe modifie avec succes');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock className="w-5 h-5 text-[#0066FF]" />
          Changer le mot de passe
        </CardTitle>
        <CardDescription>Mettez a jour votre mot de passe de connexion</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Mot de passe actuel</Label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="password-current" />
          </div>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="password-new" />
          </div>
          <div className="space-y-2">
            <Label>Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="password-confirm" />
          </div>
          <Button type="submit" disabled={loading} data-testid="password-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Modifier le mot de passe
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const RemindersSection = () => {
  const { admin } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [daysThreshold, setDaysThreshold] = useState(3);
  const [loading, setLoading] = useState(false);
  const isAdmin = admin?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      settingsApi.getReminders().then(res => {
        setEnabled(res.data.enabled ?? true);
        setDaysThreshold(res.data.days_threshold ?? 3);
      }).catch(() => {});
    }
  }, [isAdmin]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await settingsApi.updateReminders({ enabled, days_threshold: daysThreshold });
      toast.success('Parametres de rappel mis a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="w-5 h-5 text-[#0066FF]" />
          Rappels automatiques
        </CardTitle>
        <CardDescription>Envoi de rappels pour les virements en attente</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="reminders-toggle" />
          <span className="text-sm text-slate-700">{enabled ? 'Active' : 'Desactive'}</span>
        </div>
        <div className="space-y-2 max-w-xs">
          <Label>Delai avant rappel (jours)</Label>
          <Input
            type="number"
            min={1}
            max={30}
            value={daysThreshold}
            onChange={(e) => setDaysThreshold(parseInt(e.target.value) || 3)}
            data-testid="reminders-days"
          />
        </div>
        <Button onClick={handleSave} disabled={loading} data-testid="reminders-save-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Enregistrer
        </Button>
      </CardContent>
    </Card>
  );
};

const StatementsSection = () => {
  const [clientEmail, setClientEmail] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await statementsApi.generate({
        client_email: clientEmail,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      const { pdf_base64, filename, transfer_count } = res.data;
      const byteChars = atob(pdf_base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Releve genere : ${transfer_count} operations`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la generation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-5 h-5 text-[#0066FF]" />
          Releve d'operations client
        </CardTitle>
        <CardDescription>Generez un PDF avec l'historique des virements d'un client</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleGenerate} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Email du client</Label>
            <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required placeholder="client@email.com" data-testid="statement-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Date debut</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="statement-date-from" />
            </div>
            <div className="space-y-2">
              <Label>Date fin</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="statement-date-to" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="statement-generate-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Generer le releve PDF
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const SettingsPage = () => {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';

  return (
    <Layout>
      <div className="max-w-3xl" data-testid="settings-page">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl text-slate-900">Parametres</h1>
          <p className="text-slate-500 text-sm mt-1">Gerez votre compte et les parametres</p>
        </div>

        <Tabs defaultValue="security" className="space-y-6">
          <TabsList data-testid="settings-tabs">
            <TabsTrigger value="security">Securite</TabsTrigger>
            {isAdmin && <TabsTrigger value="statements">Releves</TabsTrigger>}
            {isAdmin && <TabsTrigger value="reminders">Rappels</TabsTrigger>}
          </TabsList>

          <TabsContent value="security" className="space-y-6">
            <ProfileSection />
            <TwoFactorSection />
            <PasswordSection />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="statements">
              <StatementsSection />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="reminders">
              <RemindersSection />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
